import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import * as analytics from 'analytics/Analytics';
import * as NewUserFlowActions from 'onboarding/new-user-flow/NewUserFlowActions';
import {SIGNUP_FLOW_V2_MOLBIO_OR_NOTEBOOK_SCREEN} from 'onboarding/new-user-flow/NewUserFlowConstants';
import {makeWelcomeText} from 'onboarding/new-user-flow/signup-flow-v2/InfoSidePanel';
import SignupScreen from 'onboarding/new-user-flow/signup-flow-v2/SignupScreen';
import OrganizationInvitesSection from 'orgs/OrganizationInvitesSection';
import JoinOrganizationSearch from 'parties/organizations/JoinOrganizationSearch';
import {useJoinOrganizationSearch} from 'parties/organizations/useJoinOrganizationSearch';
import EntityStore from 'stores/EntityStore';
import Col from 'ui-library/bs/Col';
import Label from 'ui-library/bs/Label';
import Row from 'ui-library/bs/Row';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY} from 'ui-library/Colors';

function NextOptions(props: {isNextButtonDisabled: boolean}): JSX.Element {
  return (
    <div className={css(styles.nextOptions)}>
      <span
        className={classNames(css(styles.skip), 'u-pointer')}
        onClick={() => {
          analytics.track('onboarding_page', 'clicked', {
            page: 'join_organization',
            objectClicked: 'button',
            objectText: 'Create or join one later',
            eventLastModified: 'FY22_Q2b3',
          });
          NewUserFlowActions.showScreen(SIGNUP_FLOW_V2_MOLBIO_OR_NOTEBOOK_SCREEN);
        }}
      >
        Create or join one later
      </span>
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        size={ButtonSize.MEDIUM}
        text="Next"
        disabled={props.isNextButtonDisabled}
        onClick={() => {
          analytics.track('onboarding_page', 'clicked', {
            page: 'join_organization',
            objectClicked: 'button',
            objectText: 'Next',
            eventLastModified: 'FY22_Q2b3',
          });
          NewUserFlowActions.showScreen(SIGNUP_FLOW_V2_MOLBIO_OR_NOTEBOOK_SCREEN);
        }}
      />
    </div>
  );
}

interface JoinOrganizationPanelOrganizationSearchProps {
  onInitialQueryChange: () => void;
  placeholderBodyText?: string;
}

function JoinOrganizationPanelOrganizationSearch(
  props: JoinOrganizationPanelOrganizationSearchProps
): JSX.Element {
  const [
    {query, filters, hasSearched, requestedJoinOrganizationIds},
    {onChangeQuery, onChangeFilters, onChangeRequestedJoinOrganizationIds},
  ] = useJoinOrganizationSearch(props.onInitialQueryChange);
  return (
    <div className={css(styles.joinOrganizationsSection)}>
      <JoinOrganizationSearch
        query={query}
        onChangeQuery={onChangeQuery}
        filters={filters}
        setFilters={onChangeFilters}
        requestedJoinOrganizationIds={requestedJoinOrganizationIds}
        onChangeRequestedJoinOrganizationIds={onChangeRequestedJoinOrganizationIds}
        placeholderBodyText={props.placeholderBodyText}
        listingHeight={148}
        hasSearched={hasSearched}
        showInstitutionFilter={true}
        showRecommendations={true}
      />
    </div>
  );
}

function JoinOrganizationPanelHeader(): JSX.Element {
  return (
    <div className={css(styles.sidePanel)}>
      <Label className="u-no-wrap">{makeWelcomeText()}</Label>
      <div className={css(styles.headerText)}>See if your lab is already on Benchling</div>
    </div>
  );
}

export default function JoinOrganizationScreen(): JSX.Element {
  const hasInvites = EntityStore.currentUserHasPendingInvites();
  const [isNextButtonDisabled, setIsNextButtonDisabled] = React.useState(!hasInvites);
  return (
    <SignupScreen>
      <Row className={css(styles.row)}>
        <Col sm={5}>
          <JoinOrganizationPanelHeader />
          <div className={classNames(css(styles.bodyText), 'u-faded')}>
            <p>
              Hundreds of labs around the world are using Benchling! Find and join your organization today.
            </p>
          </div>
        </Col>
        <Col sm={7} className={css(styles.rightPanel)}>
          {hasInvites ? (
            <div className={css(styles.joinOrganizationsSection)}>
              <div className={css(styles.bodyText)}>Pending Invites</div>
              <div className={css(styles.invitesSection)}>
                <OrganizationInvitesSection />
              </div>
            </div>
          ) : (
            <JoinOrganizationPanelOrganizationSearch
              onInitialQueryChange={() => {
                setIsNextButtonDisabled(false);
              }}
            />
          )}
          <NextOptions isNextButtonDisabled={isNextButtonDisabled} />
        </Col>
      </Row>
    </SignupScreen>
  );
}
const styles = StyleSheet.create({
  row: {
    marginLeft: '52px',
    marginRight: '46px',
  },
  sidePanel: {
    paddingTop: '83px',
  },
  headerText: {
    fontSize: '34px',
    lineHeight: '40px',
    marginTop: '15px',
  },
  bodyText: {
    fontSize: '18px',
    lineHeight: '24px',
    marginTop: '12px',
  },
  image: {
    width: '254px',
    height: '224px',
    marginLeft: '48px',
    marginTop: '77px',
    marginRight: '16px',
    marginBottom: '100px',
  },
  joinOrganizationsSection: {
    marginTop: '12px',
    marginBottom: '40px',
  },
  invitesSection: {
    border: `1px solid ${GRAY}`,
    height: '150px',
    margin: '10px 0',
    overflowY: 'scroll',
    padding: '10px',
  },
  nextOptions: {
    float: 'right',
  },
  skip: {
    marginTop: '134px',
    fontSize: '13px',
    marginRight: '22px',
    color: BLUE_LIGHT,
  },
  rightPanel: {
    marginTop: '124px',
  },
});
